import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** False where elements cannot go full screen: iPhone Safari has no Fullscreen API at all. */
export const fullscreenSupported = (doc: Document) => doc.fullscreenEnabled === true;

/** Puts the element full screen; refused (no click behind it, or a window that may not) it simply stays as it was. */
export function enterFullscreen(element: Element | null | undefined) {
  if (!element || !fullscreenSupported(element.ownerDocument)) return;
  void element.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
}

/**
 * Whether elements in the document of `ref` may go full screen. Read from that document, not the
 * main one: the pop-out call window has its own.
 */
export function useFullscreenSupported(ref: RefObject<HTMLElement | null>) {
  const [supported, setSupported] = useState(() => fullscreenSupported(document));
  useLayoutEffect(() => {
    if (ref.current) setSupported(fullscreenSupported(ref.current.ownerDocument));
  }, [ref]);
  return supported;
}

/** Full screen for one element, in whichever window it lives; Esc leaves it (the browser's own). */
export function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const supported = useFullscreenSupported(ref);
  const [active, setActive] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const doc = element.ownerDocument;
    const sync = () => setActive(doc.fullscreenElement === element);
    sync();
    doc.addEventListener('fullscreenchange', sync);
    return () => doc.removeEventListener('fullscreenchange', sync);
  }, [ref]);
  const enter = () => enterFullscreen(ref.current);
  const exit = () => {
    const doc = ref.current?.ownerDocument;
    if (doc?.fullscreenElement) void doc.exitFullscreen().catch(() => undefined);
  };
  return { supported, active, enter, exit, toggle: () => (active ? exit() : enter()) };
}

/**
 * Controls over a full-screen picture: they go after `ms` without the mouse moving and come back
 * on movement (`wake`); a touch screen has no movement, so a tap wakes them and the next `hide`s
 * them. They stay while the pointer rests on them (`hold`) or a key put focus there.
 */
export function useIdle(enabled: boolean, panel: RefObject<HTMLElement | null>, ms = 3000) {
  const [idle, setIdle] = useState(false);
  const timer = useRef(0);
  const hold = useRef(false);
  const wake = () => {
    setIdle(false);
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (hold.current || panel.current?.querySelector(':focus-visible')) wake();
      else setIdle(true);
    }, ms);
  };
  const latestWake = useRef(wake);
  latestWake.current = wake;
  useEffect(() => {
    // Left over from last time: the controls went away under the pointer, without a pointerleave.
    hold.current = false;
    if (enabled) latestWake.current();
    else setIdle(false);
    return () => clearTimeout(timer.current);
  }, [enabled]);
  const hide = () => {
    clearTimeout(timer.current);
    setIdle(true);
  };
  return {
    idle: enabled && idle,
    wake: enabled ? wake : undefined,
    hide,
    hold: {
      onPointerEnter: () => void (hold.current = true),
      onPointerLeave: () => void (hold.current = false),
    },
  };
}
